import * as R from 'ramda'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import type { ApiError, TableFilter } from '@pbt/pbt-ui-components'

import * as API from '~/api'
import { SortDirection } from '~/api/graphql/generated/types'

import { updateSearchHighlights } from '../actions/search'
import {
  CREATE_INVENTORY_ORDER_ITEM_RECEIPTS,
  type createInventoryOrderItemReceipts,
  createInventoryOrderItemReceiptsFailure,
  createInventoryOrderItemReceiptsSuccess,
  FETCH_INVENTORY_ORDER,
  FETCH_INVENTORY_ORDERS,
  FETCH_MORE_INVENTORY_ORDERS,
  type fetchInventoryOrder,
  fetchInventoryOrderFailure,
  type fetchInventoryOrders,
  fetchInventoryOrdersFailure,
  fetchInventoryOrdersSuccess,
  fetchInventoryOrderSuccess,
  type fetchMoreInventoryOrders,
  fetchMoreInventoryOrdersFailure,
  fetchMoreInventoryOrdersSuccess,
  getInventoryOrdersFilters,
  getInventoryOrdersSortings,
  updateInventoryOrderItem,
} from '../duck/inventoryOrders'
import requestAPI from './utils/requestAPI'
import updateEntities from './utils/updateEntities'

const deserializeFilters = (filters: Record<string, TableFilter>) =>
  R.mapObjIndexed(
    (filter) => filter?.value?.toString().split(',') ?? [],
    filters,
  )

const serializeSortings = (sortings: Record<string, boolean>) =>
  R.mapObjIndexed(
    (value) => (value ? SortDirection.Asc : SortDirection.Desc),
    sortings,
  )

function* fetchInventoryOrdersWorkerSaga(
  from: number,
  to: number,
  searchQuery?: string,
) {
  const [filters, sortings]: [
    Record<string, TableFilter>,
    Record<string, boolean>,
  ] = yield all([
    select(getInventoryOrdersFilters),
    select(getInventoryOrdersSortings),
  ])

  const {
    result: { data, totalCount },
    entities: { inventoryOrders },
  } = yield requestAPI(
    API.fetchInventoryOrders,
    from,
    to,
    deserializeFilters(filters),
    serializeSortings(sortings),
    searchQuery,
  )

  yield call(updateEntities, {
    inventoryOrders,
  })

  return { data, totalCount }
}

/**
 * Called on initial table load or when the filters change and will result in the existing
 * store entities being completely replaced with the new data
 */
export function* fetchInventoryOrdersSaga({
  from,
  to,
  query,
}: ReturnType<typeof fetchInventoryOrders>) {
  try {
    const { data, totalCount } = yield call(
      fetchInventoryOrdersWorkerSaga,
      from,
      to,
      query,
    )
    yield put(updateSearchHighlights(query ? [query] : [], totalCount))
    yield put(fetchInventoryOrdersSuccess(data, totalCount))
  } catch (error) {
    yield put(fetchInventoryOrdersFailure(error as ApiError))
  }
}

/**
 * Called on infinite scroll load and will result in additional inventory order entities
 * being appended in the store
 */
export function* fetchMoreInventoryOrdersSaga({
  from,
  to,
  query,
}: ReturnType<typeof fetchMoreInventoryOrders>) {
  try {
    const { data, totalCount } = yield call(
      fetchInventoryOrdersWorkerSaga,
      from,
      to,
      query,
    )

    yield put(fetchMoreInventoryOrdersSuccess(data, totalCount, from))
  } catch (error) {
    yield put(fetchMoreInventoryOrdersFailure(error as ApiError))
  }
}

export function* fetchInventoryOrderSaga({
  orderId,
}: ReturnType<typeof fetchInventoryOrder>) {
  try {
    const { entities } = yield requestAPI(API.fetchInventoryOrder, orderId)

    yield call(updateEntities, entities)
    yield put(fetchInventoryOrderSuccess(orderId))
  } catch (error) {
    yield put(fetchInventoryOrderFailure(error as ApiError))
  }
}

export function* createInventoryOrderItemReceiptsSaga({
  orderId,
  orderItemId,
  receipts,
}: ReturnType<typeof createInventoryOrderItemReceipts>) {
  try {
    const inventoryOrderItem = yield* requestAPI(
      API.addInventoryOrderItemReceipts,
      orderItemId,
      receipts,
    )

    yield put(
      updateInventoryOrderItem(orderId, orderItemId, inventoryOrderItem),
    )
    yield put(createInventoryOrderItemReceiptsSuccess(orderItemId))
  } catch (error) {
    yield put(createInventoryOrderItemReceiptsFailure(error as ApiError))
  }
}

function* watchFetchInventoryOrders() {
  yield takeLatest(FETCH_INVENTORY_ORDERS, fetchInventoryOrdersSaga)
}

function* watchFetchMoreInventoryOrders() {
  yield takeLatest(FETCH_MORE_INVENTORY_ORDERS, fetchMoreInventoryOrdersSaga)
}

function* watchFetchInventoryOrder() {
  yield takeLatest(FETCH_INVENTORY_ORDER, fetchInventoryOrderSaga)
}

function* watchAddInventoryOrderItemReceipts() {
  yield takeLatest(
    CREATE_INVENTORY_ORDER_ITEM_RECEIPTS,
    createInventoryOrderItemReceiptsSaga,
  )
}

export default function* InventoryOrdersSaga() {
  yield all([
    watchFetchInventoryOrders(),
    watchFetchMoreInventoryOrders(),
    watchFetchInventoryOrder(),
    watchAddInventoryOrderItemReceipts(),
  ])
}
